import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNode } from '../../contexts/NodeContext';
import { BellIcon, SunIcon, MoonIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { UserMenu, DemoResetBanner, useDemoReset, useSetDemoReset } from '@trinetra/shared';
import api from '../../services/api.service';
import { USER_MENU_NAV } from '../../constants/navigation';
import SiteSwitcher from './SiteSwitcher';

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

export default function Header({ onMobileMenuToggle }: HeaderProps) {
  const { user, logout, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  // Null unless the SELECTED site announces a demo wipe cycle.
  const demoReset = useDemoReset();
  const setDemoReset = useSetDemoReset();
  const { selectedNodeId } = useNode();

  // Drop the banner the moment the scope changes, and let the newly selected site put it
  // back if it is a demo. Two cases this covers that the board's own reset cannot:
  //   • All Sites (selectedNodeId null) — the banner speaks for one site's cycle, and across
  //     an estate where one node is a demo it would be a claim about all of them;
  //   • a switch made from a page with no alert board mounted — nothing else would be
  //     listening, so a demo site's countdown would sit over a real site until a board opened.
  // Clearing is the safe direction: a missing banner understates, a stale one misinforms.
  useEffect(() => {
    setDemoReset(null);
  }, [selectedNodeId, setDemoReset]);

  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const res = await api.get('/alert-system/summary');
        const data = res.data?.data || res.data;
        setAlertCount(data.unacknowledged_count || 0);
      } catch { /* ignore */ }
    };
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    // z-40, not z-30: <main> sets no stacking context, so page content escapes into the root
    // one and competes with this header directly. The alert panel's live tiles and the board's
    // click-preview reach z-30 and, coming later in the DOM, were painting over the user-menu
    // dropdown. Modals and the layout dropdown stay at z-50, so they still cover the header —
    // which is what they are for.
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-6 sticky top-0 z-40">
      {/* Left — mobile hamburger, then the scope control. The switcher lives here rather than
          in the sidebar because it governs everything in the sidebar: on screen permanently,
          above the menu it scopes, and it can never scroll out of view. */}
      <div className="flex min-w-0 items-center gap-3">
        <button
          onClick={onMobileMenuToggle}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors lg:hidden"
          title="Toggle menu"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
        <SiteSwitcher />
        {/* On a demo, the one thing every page has to say: these alerts, events and attendance
            rows are wiped on a timer, and here is how long is left. In the title bar because
            it is true of the whole site, not of any one page. */}
        <DemoResetBanner info={demoReset} className="hidden lg:flex" />
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Narrow screens: the countdown alone — the sentence is in its tooltip. */}
        <DemoResetBanner info={demoReset} compact className="lg:hidden" />
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
        </button>

        {/* Notifications */}
        <button
          onClick={() => navigate('/system-alerts')}
          className="relative rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title="System Alerts"
        >
          <BellIcon className="h-5 w-5" />
          {alertCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {alertCount > 99 ? '99+' : alertCount}
            </span>
          )}
        </button>

        {/* User menu — platform admin surfaces + logout */}
        <UserMenu
          items={USER_MENU_NAV}
          hasPermission={hasPermission}
          user={{ name: user?.name, role: user?.role }}
          onLogout={logout}
        />
      </div>
    </header>
  );
}
